/**
 * WhyAgentStoppedNote — info banner explaining why the runtime quality reviewer stopped the
 * agent (a forced hold in the Agent Response Queue, or a reviewer-raised escalation). Shows the
 * reviewer's critique and the playbook item it names; read-only, no actions.
 */

import { useId } from 'react';
import type { QualityReviewBlock } from '@resida/api-types/agent';

type Tier = QualityReviewBlock['tier'];

const TIER_PREFIX: Record<Tier, string> = {
  locked: 'Always applies:',
  rule: 'Your rule:',
  default: 'Default:',
  internal: 'Resida check:',
};

export interface WhyAgentStoppedNoteProps {
  critique: string;
  tier: Tier;
  /** Text of the item the critique cites; null when it could not be resolved. */
  itemText: string | null;
  /** For an escalation that blocked an action: summary of what the agent wanted to do. */
  blockedArgsSummary?: string;
  'data-testid': string;
}

export function WhyAgentStoppedNote({
  critique,
  tier,
  itemText,
  blockedArgsSummary,
  'data-testid': dataTestId,
}: WhyAgentStoppedNoteProps) {
  const labelId = useId();
  return (
    <div
      role="note"
      aria-labelledby={labelId}
      data-testid={dataTestId}
      className="mb-3 rounded-md border border-info/25 bg-info/10 p-3 text-sm text-foreground"
    >
      <p id={labelId} className="mb-1 font-semibold text-info">Why the agent stopped</p>
      {itemText && (
        <p className="mb-2">
          <span className="font-medium">{TIER_PREFIX[tier]}</span> {itemText}
        </p>
      )}
      <p className="whitespace-pre-wrap border-l-2 border-info/40 pl-3 italic">{critique}</p>
      {blockedArgsSummary && (
        <p className="mt-2 text-sm text-muted-foreground">The agent wanted to: {blockedArgsSummary}</p>
      )}
    </div>
  );
}

export default WhyAgentStoppedNote;
